import { useState, useEffect, useMemo, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes, faSpinner, faDownload, faTriangleExclamation, faCheck, faRotate,
} from '@fortawesome/free-solid-svg-icons';
import { api, getApiUrl } from '../utils/apiConfig';
import { useChannelConfigs } from '../hooks/useChannelConfigs';
import { getEnabledPlatforms } from '../utils/accountSettingPlatformList';
import { getFolderDisplayLabel } from '../utils/folderHierarchy';
import './EditCampaignModal.css';
import './ImportAllCampaignsModal.css';

/**
 * Import every campaign on one ad account, in one action.
 *
 * REVIEW BEFORE IMPORT, ALWAYS
 * Filling the form runs a DISCOVERY call (POST /campaigns/import/discover)
 * that reads the platform and writes nothing. The import button stays
 * unreachable until that list is on screen. This is the same ordering as
 * GroupEditModal's preview-then-apply, for the same reason: "import all" is a
 * fan-out whose size the user cannot know in advance, and a mistyped customer
 * id or the wrong status should cost a read, not fifty campaign records.
 *
 * Already-imported campaigns are shown but unchecked and excluded by default —
 * re-importing one would create a second row for the same platform campaign.
 */

/**
 * The four channels the discovery service can enumerate. Google Ads serves
 * Search, PMax and Demand Gen from one campaign endpoint, so they are separate
 * choices HERE and separated by advertising_channel_type on the server; they
 * are not separate platform APIs.
 *
 * `settingKey` is the spelling the `import-campaign` account setting uses (the
 * same destination ids CampaignCreation's import mode filters on), so this
 * modal offers exactly the channels that account is allowed to import from.
 */
const IMPORT_CHANNELS = [
  { id: 'facebook', label: 'Facebook', accountIdLabel: 'Ad Account ID', accountIdHint: 'e.g. act_821587643709084 — the act_ prefix is optional' },
  { id: 'google-ads-search', label: 'Google Search', accountIdLabel: 'Customer ID', accountIdHint: 'e.g. 3825705313 — hyphens optional' },
  { id: 'google-ads-pmax', label: 'Google PMax', accountIdLabel: 'Customer ID', accountIdHint: 'e.g. 3825705313 — hyphens optional' },
  { id: 'google-ads-demandgen', label: 'Google Demand Gen', accountIdLabel: 'Customer ID', accountIdHint: 'e.g. 3825705313 — hyphens optional' },
];

/**
 * Our status vocabulary, not the platforms'. Neither Google nor Meta has an
 * "ended" status — a campaign past its flight still reports ENABLED/ACTIVE —
 * so the server derives these three from the platform status plus the end
 * date. Mirror of CAMPAIGN_STATUS_FILTERS in
 * services/platformCampaignDiscovery.js.
 */
const STATUS_OPTIONS = [
  { id: 'active', label: 'Active', hint: 'Enabled, and still within its flight dates' },
  { id: 'paused', label: 'Paused', hint: 'Paused on the platform' },
  { id: 'ended', label: 'Ended', hint: 'Past its end date, however it is set' },
  { id: 'all', label: 'All', hint: 'Every campaign that still exists on the account' },
];

/**
 * The reason the server gave, not axios's description of the status code.
 * Every 4xx these routes return carries an actionable message ("No Google Ads
 * channel is configured on this account"); `err.message` would show "Request
 * failed with status code 409" and throw all of that away.
 */
const describeRequestError = (err) => (
  err?.response?.data?.error || err?.message || 'Something went wrong'
);

/**
 * The channel configs that can serve one destination.
 *
 * Facebook maps 1:1 onto its channel type. Google Ads does NOT: credentials
 * for a Google Ads ACCOUNT (the yaml_string / refresh token / developer token)
 * are the same whether you are reading its Search, PMax or Demand Gen
 * campaigns, and accounts configure them under whichever of the
 * `google-ads*` channel types they happened to set up first — often a generic
 * `google-ads`. The discovery service already accepts any of them
 * (`ct.name LIKE 'google-ads%'`).
 *
 * Keying the picker strictly on the destination id made the frontend NARROWER
 * than the backend: an account whose credentials live on `google-ads` saw an
 * empty picker when importing Search, the request went out with no channelId,
 * and the server fell back to whichever Google channel it updated last — which
 * is how a correctly-configured account got "Google Ads authorization has not
 * completed for this channel".
 */
function configsForDestination(channelConfigs, destination) {
  if (!destination || !channelConfigs) return [];
  if (!destination.startsWith('google-ads')) return channelConfigs[destination] || [];
  return Object.entries(channelConfigs)
    .filter(([type]) => type.startsWith('google-ads'))
    .flatMap(([type, configs]) => (configs || []).map((config) => ({ ...config, type })));
}

/**
 * The account id stored on a channel, under either spelling — for the option
 * LABEL only, mirroring how the create/import form labels its channel picker.
 *
 * Deliberately not used to fill the Customer ID / Ad Account ID field. The two
 * answer different questions: the channel config supplies the CREDENTIALS, and
 * the id field says WHICH account to read with them. One channel's credentials
 * routinely reach several customer ids under the same MCC, so treating the
 * stored id as the one being imported would be wrong more often than helpful.
 */
const configAccountId = (config) => config?.field_values?.customer_id
  || config?.field_values?.ad_account_id
  || null;

const ImportAllCampaignsModal = ({
  accountId,
  user,
  folders = [],
  accountSettings,
  defaultFolderId = null,
  onClose,
  onImported,
}) => {
  const { channelConfigs: allChannelConfigs, isLoading: channelConfigsLoading } = useChannelConfigs(accountId, user);

  const [channel, setChannel] = useState('');
  const [channelId, setChannelId] = useState('');
  const [platformAccountId, setPlatformAccountId] = useState('');
  const [status, setStatus] = useState('active');
  const [folderId, setFolderId] = useState(defaultFolderId || '');

  const [busy, setBusy] = useState(null); // 'discover' | 'import'
  const [discovery, setDiscovery] = useState(null);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  // Only offer channels this account is allowed to import from. Same
  // `import-campaign` setting (and same alias-tolerant parser) that gates the
  // single-campaign import form, so the two entry points can never disagree.
  const availableChannels = useMemo(() => {
    const enabled = getEnabledPlatforms(accountSettings?.['import-campaign']);
    return IMPORT_CHANNELS.filter((option) => enabled.includes(option.id));
  }, [accountSettings]);

  const channelMeta = IMPORT_CHANNELS.find((option) => option.id === channel) || null;
  const configs = useMemo(
    () => configsForDestination(allChannelConfigs, channel),
    [allChannelConfigs, channel],
  );

  // Any change to what would be fetched invalidates the list on screen. Left
  // as an effect rather than folded into each setter so a field added later
  // cannot forget to do it — a stale list is what would let someone import
  // from an account they have since edited out of the form.
  useEffect(() => {
    setDiscovery(null);
    setSelectedIds(new Set());
    setResult(null);
  }, [channel, channelId, platformAccountId, status]);

  // Default to the only config when there is exactly one, but still SHOW it —
  // which channel's credentials a bulk import reads is part of what the user
  // is confirming, not a detail to hide because the answer is currently
  // unambiguous. Clear the selection when it no longer belongs to the chosen
  // channel so a stale id can never ride along with a different destination.
  useEffect(() => {
    if (configs.length === 1) setChannelId(configs[0].id);
    else if (!configs.some((config) => config.id === channelId)) setChannelId('');
  }, [channel, configs]); // eslint-disable-line react-hooks/exhaustive-deps

  // Always required when the account HAS a config for this channel. The old
  // rule ("only ask when there is more than one") let the request go out with
  // no channelId and leave the server guessing.
  const canDiscover = Boolean(channel && platformAccountId.trim() && status)
    && (configs.length === 0 || Boolean(channelId))
    && !busy;

  const selectableCampaigns = discovery?.campaigns || [];
  const newCampaigns = selectableCampaigns.filter((campaign) => !campaign.already_imported);

  const handleDiscover = useCallback(async () => {
    setBusy('discover');
    setError(null);
    setResult(null);
    try {
      const { data } = await api.post(getApiUrl('/api/v2/campaigns/import/discover'), {
        accountId,
        platform: channel,
        platformAccountId: platformAccountId.trim(),
        status,
        ...(channelId && { channelId }),
      }, { params: { accountId, userId: user?.userId || user?.id } });
      setDiscovery(data);
      // Pre-check exactly what will import: everything new, nothing already in.
      setSelectedIds(new Set(
        (data.campaigns || [])
          .filter((campaign) => !campaign.already_imported)
          .map((campaign) => campaign.platform_campaign_id),
      ));
    } catch (err) {
      setError(describeRequestError(err));
      setDiscovery(null);
    } finally {
      setBusy(null);
    }
  }, [accountId, channel, channelId, platformAccountId, status, user]);

  const handleImport = useCallback(async () => {
    setBusy('import');
    setError(null);
    try {
      const { data } = await api.post(getApiUrl('/api/v2/campaigns/import/all'), {
        accountId,
        platform: channel,
        platformAccountId: platformAccountId.trim(),
        status,
        folderId,
        ...(channelId && { channelId }),
        platformCampaignIds: Array.from(selectedIds),
      }, { params: { accountId, userId: user?.userId || user?.id } });
      setResult(data);
      // Refresh the page behind the modal even on a partial failure — the
      // campaigns that DID start importing are already rows the user should see.
      if (data.counts?.imported > 0) onImported?.(data);
    } catch (err) {
      setError(describeRequestError(err));
    } finally {
      setBusy(null);
    }
  }, [accountId, channel, channelId, platformAccountId, status, folderId, selectedIds, user, onImported]);

  const toggleCampaign = (platformCampaignId) => {
    setSelectedIds((previous) => {
      const next = new Set(previous);
      if (next.has(platformCampaignId)) next.delete(platformCampaignId);
      else next.add(platformCampaignId);
      return next;
    });
  };

  const allNewSelected = newCampaigns.length > 0
    && newCampaigns.every((campaign) => selectedIds.has(campaign.platform_campaign_id));

  const toggleAll = () => {
    setSelectedIds(allNewSelected
      ? new Set()
      : new Set(newCampaigns.map((campaign) => campaign.platform_campaign_id)));
  };

  const canImport = Boolean(folderId) && selectedIds.size > 0 && !busy && !result;

  return (
    <div className="modal-overlay" onClick={() => !busy && onClose()}>
      <div className="modal-content import-all-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>
            <FontAwesomeIcon icon={faDownload} />
            {' '}Import all campaigns
          </h2>
          <button className="modal-close" onClick={() => onClose()} disabled={!!busy}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="modal-body">
          {availableChannels.length === 0 ? (
            <div className="import-all-error">
              <FontAwesomeIcon icon={faTriangleExclamation} />
              <span>
                No channel on this account is enabled for campaign import. An account
                admin enables them in the <strong>import-campaign</strong> account setting.
              </span>
            </div>
          ) : (
            <>
              <div className="import-all-fields">
                <div className="import-all-field">
                  <label htmlFor="import-all-channel">Channel *</label>
                  <select
                    id="import-all-channel"
                    className="form-control"
                    value={channel}
                    onChange={(e) => setChannel(e.target.value)}
                    disabled={!!busy}
                  >
                    <option value="">Select a channel</option>
                    {availableChannels.map((option) => (
                      <option key={option.id} value={option.id}>{option.label}</option>
                    ))}
                  </select>
                </div>

                <div className="import-all-field">
                  <label htmlFor="import-all-status">Status *</label>
                  <select
                    id="import-all-status"
                    className="form-control"
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    disabled={!!busy}
                  >
                    {STATUS_OPTIONS.map((option) => (
                      <option key={option.id} value={option.id}>{option.label}</option>
                    ))}
                  </select>
                  <p className="import-all-hint">
                    {STATUS_OPTIONS.find((option) => option.id === status)?.hint}
                  </p>
                </div>
              </div>

              {/* Always shown once a channel is picked, even when there is
                  only one config. The credentials this reads live ON the
                  channel, so which one is used decides whether the import can
                  authenticate at all — that belongs on screen, not inferred. */}
              {channel && (
                <div className="import-all-field">
                  <label htmlFor="import-all-channel-config">Channel configuration *</label>
                  {channelConfigsLoading ? (
                    <p className="import-all-hint">Loading channel configurations…</p>
                  ) : configs.length === 0 ? (
                    <div className="import-all-warning">
                      <FontAwesomeIcon icon={faTriangleExclamation} />
                      <span>
                        No {channelMeta.label} channel is configured on this account.
                        Add one under Channels before importing.
                      </span>
                    </div>
                  ) : (
                    <>
                      <select
                        id="import-all-channel-config"
                        className="form-control"
                        value={channelId}
                        onChange={(e) => setChannelId(e.target.value)}
                        disabled={!!busy}
                      >
                        <option value="">Select a channel configuration</option>
                        {configs.map((config) => (
                          <option key={config.id} value={config.id}>
                            {/* The stored account id disambiguates two configs
                                with the same name; the channel type shows that
                                a Search import may legitimately read
                                credentials off a `google-ads` channel. */}
                            {config.name}
                            {configAccountId(config) ? ` — ${configAccountId(config)}` : ''}
                            {config.type && config.type !== channel ? ` (${config.type})` : ''}
                          </option>
                        ))}
                      </select>
                      <p className="import-all-hint">
                        Supplies the credentials used to read the platform. Google Ads
                        channels share credentials across Search, PMax and Demand Gen,
                        so any of them may be the right one. Which account to import
                        from is the {channelMeta.accountIdLabel} below.
                      </p>
                    </>
                  )}
                </div>
              )}

              {channel && (
                <div className="import-all-field">
                  <label htmlFor="import-all-account-id">{channelMeta.accountIdLabel} *</label>
                  <input
                    id="import-all-account-id"
                    type="text"
                    className="form-control"
                    value={platformAccountId}
                    placeholder={channelMeta.accountIdLabel}
                    onChange={(e) => setPlatformAccountId(e.target.value)}
                    disabled={!!busy}
                  />
                  <p className="import-all-hint">
                    {channelMeta.accountIdHint}. The account to import from — the
                    channel configuration above supplies the credentials to reach it.
                  </p>
                </div>
              )}

              <div className="import-all-field">
                <label htmlFor="import-all-folder">Import into folder *</label>
                <select
                  id="import-all-folder"
                  className="form-control"
                  value={folderId}
                  onChange={(e) => setFolderId(e.target.value)}
                  disabled={!!busy}
                >
                  <option value="">Select a folder</option>
                  {folders.map((folder) => (
                    <option key={folder.id} value={folder.id}>
                      {getFolderDisplayLabel(folder)}
                    </option>
                  ))}
                </select>
              </div>

              {discovery && (
                <div className="import-all-results">
                  <div className="import-all-results-header">
                    <h3>
                      <FontAwesomeIcon icon={faCheck} />
                      {' '}
                      {discovery.total} campaign{discovery.total === 1 ? '' : 's'} found
                      {discovery.alreadyImportedCount > 0
                        && ` — ${discovery.alreadyImportedCount} already imported`}
                    </h3>
                    {newCampaigns.length > 0 && (
                      <button type="button" className="import-all-toggle-all" onClick={toggleAll} disabled={!!busy}>
                        {allNewSelected ? 'Clear all' : 'Select all'}
                      </button>
                    )}
                  </div>

                  {discovery.total === 0 && (
                    <p className="import-all-hint">
                      {discovery.totalBeforeStatusFilter > 0
                        ? `This account has ${discovery.totalBeforeStatusFilter} campaign${discovery.totalBeforeStatusFilter === 1 ? '' : 's'} on this channel, but none are ${status === 'all' ? 'importable' : status}.`
                        : 'No campaigns on this channel for that account.'}
                    </p>
                  )}

                  {discovery.truncated && (
                    <div className="import-all-warning">
                      <FontAwesomeIcon icon={faTriangleExclamation} />
                      <span>{discovery.warning}</span>
                    </div>
                  )}

                  {discovery.total > 0 && (
                    <ul className="import-all-campaigns">
                      {selectableCampaigns.map((campaign) => (
                        <li key={campaign.platform_campaign_id}>
                          <label
                            className={campaign.already_imported ? 'import-all-campaign imported' : 'import-all-campaign'}
                            title={campaign.already_imported ? 'Already imported into this account' : undefined}
                          >
                            <input
                              type="checkbox"
                              checked={selectedIds.has(campaign.platform_campaign_id)}
                              onChange={() => toggleCampaign(campaign.platform_campaign_id)}
                              disabled={!!busy || !!result}
                            />
                            <span className="import-all-campaign-name">
                              {campaign.name || campaign.platform_campaign_id}
                            </span>
                            <span className="import-all-campaign-meta">
                              {campaign.platform_campaign_id}
                              {' · '}
                              {campaign.normalized_status}
                              {campaign.end_date ? ` · ends ${campaign.end_date}` : ''}
                              {campaign.already_imported ? ' · already imported' : ''}
                            </span>
                          </label>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              {result && (
                <div className="import-all-results">
                  <h3>
                    <FontAwesomeIcon icon={result.success ? faCheck : faTriangleExclamation} />
                    {' '}{result.message}
                  </h3>
                  <p className="import-all-hint">
                    Imports run in the background — the campaigns appear in the list
                    straight away and fill in as each one finishes.
                  </p>
                  {result.failed?.length > 0 && (
                    <ul className="import-all-failures">
                      {result.failed.map((failure) => (
                        <li key={failure.platformCampaignId}>
                          <strong>{failure.name || failure.platformCampaignId}</strong>: {failure.error}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              {error && (
                <div className="import-all-error">
                  <FontAwesomeIcon icon={faTriangleExclamation} />
                  <span>{error}</span>
                </div>
              )}
            </>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={() => onClose()} disabled={!!busy}>
            {result ? 'Done' : 'Cancel'}
          </button>
          <button
            className="btn btn-secondary"
            onClick={handleDiscover}
            disabled={!canDiscover}
            title={canDiscover ? 'Read the campaigns on this account' : 'Choose a channel, account id and status first'}
          >
            {busy === 'discover'
              ? <><FontAwesomeIcon icon={faSpinner} spin /> Finding campaigns…</>
              : <><FontAwesomeIcon icon={discovery ? faRotate : faCheck} /> {discovery ? 'Refresh list' : 'Find campaigns'}</>}
          </button>
          <button
            className="btn btn-primary"
            onClick={handleImport}
            // Import is only reachable from a list the user has seen. Not a
            // convenience gate: it is what stops a mistyped account id from
            // becoming fifty campaign records.
            disabled={!canImport}
            title={discovery ? 'Import the selected campaigns' : 'Find the campaigns first'}
          >
            {busy === 'import'
              ? <><FontAwesomeIcon icon={faSpinner} spin /> Importing…</>
              : `Import ${selectedIds.size || ''} campaign${selectedIds.size === 1 ? '' : 's'}`.replace('  ', ' ')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImportAllCampaignsModal;
